import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth/useAuth'

// Minimal Paddle.js v2 typings — only the surface this page actually uses.
interface PaddleProduct {
  id: string | number
  tier_name: string
}

interface PaddleEventData {
  name: string
}

interface PaddleCheckoutOptions {
  transactionId?: string
  items?: { priceId: string; quantity: number }[]
  customData?: Record<string, unknown>
  customer?: { id?: string; email?: string; name?: string }
  settings: {
    displayMode?: string
    theme?: string
    locale?: string
    allowLogout?: boolean
  }
}

interface PaddleApi {
  Environment: { set: (env: string) => void }
  Initialize: (opts: { token: string; eventCallback?: (data: PaddleEventData) => void }) => void
  Checkout: { open: (opts: PaddleCheckoutOptions) => void }
}

// Declare Paddle global type
declare global {
  interface Window {
    Paddle: PaddleApi
  }
}

const CheckoutPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [status, setStatus] = useState<'loading' | 'redirecting' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [productIds, setProductIds] = useState<Record<string, PaddleProduct>>({})
  const [hasCheckedSubscription, setHasCheckedSubscription] = useState(false)
  const checkoutCompletedRef = useRef(false)

  const plan = searchParams.get('plan')
  const type = searchParams.get('type') // 'callpack' or subscription
  const paddleIdFromUrl = searchParams.get('paddle_id')
  const packSize = searchParams.get('pack_size')
  const productId = searchParams.get('product_id') // Product ID for call packs
  const quantity = parseInt(searchParams.get('quantity') || '1') || 1 // Quantity for credit purchases
  const transactionId = searchParams.get('_ptxn') // Paddle transaction ID
  const preCheckedEmail = searchParams.get('email') // Email from landing page pre-check
  const preCheckedName = searchParams.get('name') // Name from landing page pre-check

  // Helper function to initialize Paddle with transaction ID
  const initializePaddleWithTransaction = useCallback(
    (txnId: string) => {
      try {
        // Initialize Paddle with environment from config
        const paddleEnv = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox'
        window.Paddle.Environment.set(paddleEnv)
        window.Paddle.Initialize({
          token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN,
          eventCallback: (data: PaddleEventData) => {
            console.log('Paddle event:', data)

            if (data.name === 'checkout.completed') {
              checkoutCompletedRef.current = true
              navigate('/checkout/success?type=callpack')
            } else if (data.name === 'checkout.closed') {
              // Only redirect if checkout wasn't completed
              if (!checkoutCompletedRef.current) {
                navigate('/dashboard?tab=usage')
              }
            }
          },
        })

        setStatus('redirecting')

        // Open checkout with transaction ID and prevent email change
        // Transaction flow only happens for authenticated users, so always lock email
        window.Paddle.Checkout.open({
          transactionId: txnId,
          settings: {
            displayMode: 'overlay',
            theme: 'dark',
            locale: 'en',
            // CRITICAL: Transactions are user-specific, always lock email
            allowLogout: false,
          },
        })
      } catch (error) {
        console.error('Paddle checkout error:', error)
        setStatus('error')
        setErrorMessage((error as Error).message || 'Failed to open Paddle checkout')
      }
    },
    [navigate],
  )

  // Helper function to open Paddle checkout with transaction
  const openPaddleCheckoutWithTransaction = useCallback(
    (txnId: string) => {
      try {
        setStatus('loading')

        // Load Paddle.js if not already loaded
        if (!window.Paddle) {
          const script = document.createElement('script')
          script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
          script.async = true

          script.onload = () => {
            initializePaddleWithTransaction(txnId)
          }

          script.onerror = () => {
            setStatus('error')
            setErrorMessage('Failed to load Paddle checkout')
          }

          document.body.appendChild(script)
        } else {
          initializePaddleWithTransaction(txnId)
        }
      } catch (error) {
        console.error('Paddle checkout error:', error)
        setStatus('error')
        setErrorMessage((error as Error).message || 'Failed to open Paddle checkout')
      }
    },
    [initializePaddleWithTransaction],
  )

  // CRITICAL: Check if user already has active subscription before allowing checkout
  useEffect(() => {
    const checkExistingSubscription = async () => {
      // Only check for subscription products, not call packs
      if (type === 'callpack') {
        setHasCheckedSubscription(true)
        return
      }

      // If user is logged in, check if they have an active subscription
      if (user?.uuid) {
        try {
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8880/api/v1'
          const token = localStorage.getItem('auth_token')

          const response = await fetch(`${apiBaseUrl}/subscription_changes/${user.uuid}/status`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          if (response.ok) {
            const data = await response.json()

            // If user has an active paid subscription (not Free tier)
            if (data.status === 'active' && data.current_tier_id > 1) {
              setStatus('error')
              setErrorMessage(
                'You already have an active subscription! ' +
                  'Please upgrade or downgrade from your Dashboard instead of creating a new subscription.',
              )
              return
            }
          }
        } catch (error) {
          console.error('Error checking subscription:', error)
          // Continue anyway - don't block checkout if check fails
        }
      }

      setHasCheckedSubscription(true)
    }

    checkExistingSubscription()
  }, [user, type])

  useEffect(() => {
    // Wait for subscription check to complete
    if (!hasCheckedSubscription) return

    // If we're in error state (existing subscription), don't proceed
    if (status === 'error') return

    // If we have a transaction ID, open Paddle checkout with it
    if (transactionId) {
      openPaddleCheckoutWithTransaction(transactionId)
      return
    }

    // For call packs, we don't need to fetch product IDs
    if (type === 'callpack') {
      return
    }

    // Otherwise, fetch product IDs for regular subscription flow
    const fetchProductIds = async () => {
      try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8880/api/v1'
        const response = await fetch(`${apiBaseUrl}/paddle/products`)
        if (!response.ok) throw new Error('Failed to fetch products')
        const data = await response.json()

        // Transform array to map by tier_name for easy lookup
        const productsMap: Record<string, PaddleProduct> = {}
        data.products.forEach((product: PaddleProduct) => {
          productsMap[product.tier_name] = product
        })
        setProductIds(productsMap)
      } catch (error) {
        console.error('Error fetching product IDs:', error)
      }
    }
    fetchProductIds()
  }, [transactionId, type, hasCheckedSubscription, status, openPaddleCheckoutWithTransaction])

  useEffect(() => {
    // Wait for products to load for subscription flow
    if (type !== 'callpack' && Object.keys(productIds).length === 0) return

    const initiateCheckout = async () => {
      try {
        setStatus('loading')

        // Get paddle_id from URL first, then fall back to session storage
        const paddleId = paddleIdFromUrl || sessionStorage.getItem('selected_paddle_id')

        if (!paddleId) {
          throw new Error('No plan selected. Please select a plan from the pricing page.')
        }

        console.log('Opening Paddle checkout with price_id:', paddleId)

        // Load Paddle.js if not already loaded
        if (!window.Paddle) {
          const script = document.createElement('script')
          script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
          script.async = true

          script.onload = () => {
            openPaddleCheckout(paddleId)
          }

          script.onerror = () => {
            throw new Error('Failed to load Paddle checkout')
          }

          document.body.appendChild(script)
        } else {
          openPaddleCheckout(paddleId)
        }
      } catch (error) {
        console.error('Checkout error:', error)
        setStatus('error')

        // Extract detailed error message
        let errorDetail = 'Failed to initialize checkout'

        if ((error as Error).message) {
          errorDetail = (error as Error).message
        }

        setErrorMessage(errorDetail)
      }
    }

    const openPaddleCheckout = (priceId: string) => {
      try {
        // Initialize Paddle with environment from config
        const paddleEnv = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox'
        window.Paddle.Environment.set(paddleEnv)
        window.Paddle.Initialize({
          token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN,
          eventCallback: (data: PaddleEventData) => {
            console.log('Paddle event:', data)

            if (data.name === 'checkout.completed') {
              checkoutCompletedRef.current = true
              // Redirect to success page - handle both subscription and call pack flows
              if (type === 'callpack') {
                navigate(`/checkout/success?type=callpack&pack_size=${packSize}`)
              } else {
                navigate('/checkout/success')
              }
            } else if (data.name === 'checkout.closed') {
              // Only redirect if checkout wasn't completed
              if (!checkoutCompletedRef.current) {
                // User closed checkout - go back appropriately
                if (type === 'callpack') {
                  navigate('/dashboard?tab=usage')
                } else {
                  navigate('/#pricing')
                }
              }
            }
          },
        })

        setStatus('redirecting')

        // Prepare checkout options
        const checkoutOptions: PaddleCheckoutOptions = {
          items: [
            {
              priceId: priceId,
              quantity: type === 'callpack' ? quantity : 1,
            },
          ],
          customData: {
            // Always pass product_id - for subscriptions from productIds map, for call packs from URL
            product_id:
              type === 'callpack' ? productId : productIds[plan || '']?.id?.toString() || '',
          },
          settings: {
            displayMode: 'overlay',
            theme: 'dark',
            locale: 'en',
            // CRITICAL: Always prevent email change for authenticated users
            allowLogout: user ? false : true,
          },
        }

        // If user has Paddle customer ID, use it to lock the customer
        if (user?.paddle_customer_id) {
          checkoutOptions.customer = {
            id: user.paddle_customer_id,
          }
        } else if (preCheckedEmail) {
          // Email from landing page pre-check - prefill and lock it
          checkoutOptions.customer = {
            email: preCheckedEmail,
          }
          // Add name if provided from pre-check
          if (preCheckedName) {
            checkoutOptions.customer.name = preCheckedName
          }
          // CRITICAL: Don't allow email change since it was pre-verified
          checkoutOptions.settings.allowLogout = false
        } else if (user?.email) {
          // Logged in user without paddle_customer_id - prefill and lock email
          checkoutOptions.customer = {
            email: user.email,
          }
          // CRITICAL: Always lock email for authenticated users
          checkoutOptions.settings.allowLogout = false
        }

        // Open checkout overlay
        window.Paddle.Checkout.open(checkoutOptions)
      } catch (error) {
        console.error('Paddle checkout error:', error)
        setStatus('error')
        setErrorMessage((error as Error).message || 'Failed to open Paddle checkout')
      }
    }

    // Check conditions for initiating checkout
    // For call packs: need type, paddle_id, and product_id
    // For subscriptions: need plan and paddle_id
    const shouldInitiateCheckout =
      (type === 'callpack' && paddleIdFromUrl && productId) || (plan && paddleIdFromUrl)

    if (shouldInitiateCheckout) {
      initiateCheckout()
    } else {
      setStatus('error')
      if (type === 'callpack') {
        if (!productId) {
          setErrorMessage('Invalid call pack checkout - missing product ID')
        } else {
          setErrorMessage('Invalid call pack checkout - missing price information')
        }
      } else {
        setErrorMessage('No plan specified')
      }
    }
  }, [
    plan,
    type,
    paddleIdFromUrl,
    packSize,
    navigate,
    productIds,
    productId,
    quantity,
    user,
    preCheckedEmail,
    preCheckedName,
  ])

  if (status === 'loading' || status === 'redirecting') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <CardTitle>
              {status === 'loading' ? 'Preparing Checkout...' : 'Redirecting to Payment...'}
            </CardTitle>
            <CardDescription>
              {status === 'loading'
                ? `Setting up your ${type === 'callpack' ? 'call pack' : 'subscription'} checkout`
                : 'You will be redirected to Paddle checkout in a moment'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center text-sm text-muted-foreground">
            {type === 'callpack' ? (
              <p>
                Selected: <span className="font-semibold">{packSize} credits</span>
              </p>
            ) : (
              <p>
                Selected plan: <span className="font-semibold capitalize">{plan}</span>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (status === 'error') {
    // Check if this is the "already has subscription" error
    const hasActiveSubscription = errorMessage.includes('already have an active subscription')

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md border-destructive">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-destructive">
              {hasActiveSubscription ? 'Active Subscription Detected' : 'Checkout Error'}
            </CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasActiveSubscription ? (
              <>
                <Button className="w-full" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
                <Button className="w-full" onClick={() => navigate('/#pricing')} variant="outline">
                  Back to Pricing
                </Button>
              </>
            ) : (
              <Button
                className="w-full"
                onClick={() => navigate(type === 'callpack' ? '/dashboard?tab=usage' : '/#pricing')}
                variant="outline"
              >
                {type === 'callpack' ? 'Back to Dashboard' : 'Back to Pricing'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  return null
}

export default CheckoutPage
